
import { Vue, Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { Modules } from '../../store/store';
import {
  BatteryChangingInformation,
  BatteryChangingInformationSeparated,
  ChargingOption as ChargingOptionInterface,
  Vehicle,
} from '../../store/modules/charging-data.interface';
import { Locale } from '../../store/modules/config';
import { timeConvert } from '../../utils/time-helpers';
import { SubBrand } from '@/store/modules/themes';

const themeModule = namespace(Modules.themes);
const dataModule = namespace(Modules.chargingData);
const configModule = namespace(Modules.config);

@Component({
  name: 'eqweb-charging-options-grid-functions.',
})
export default class ChargingOptionsGridFunctions extends Vue {
  @themeModule.Getter('subBrand')
  protected readonly subBrand!: SubBrand;

  @configModule.Getter('asSidebar')
  protected readonly asSidebar!: boolean;

  @dataModule.Getter('hasPublicChargingOptions')
  protected readonly hasPublicChargingOptions!: boolean;

  @dataModule.Getter('hasPrivateChargingOptions')
  protected readonly hasPrivateChargingOptions!: boolean;

  @dataModule.Getter('batteryChangingInformationSeparated')
  protected readonly batteryChangingInformationSeparated!: BatteryChangingInformationSeparated;

  @dataModule.State('selectedVehicle')
  protected readonly selectedVehicle!: Vehicle;

  @configModule.State('locale')
  protected readonly locale!: Locale;

  protected dcChargingPowerIsAvailable!: boolean;

  @Watch('selectedVehicle')
  private onVehicleChanged(selectedVehicle: Vehicle): void {
    this.dcChargingPowerIsAvailable = !(selectedVehicle.installedDcChargingPower === undefined);
  }

  private created(): void {
    this.dcChargingPowerIsAvailable = !(this.selectedVehicle.installedDcChargingPower === undefined);
  }

  protected replaceTempVars(
    baseText: string | undefined,
    replaceObj: { [key: string]: string | undefined },
  ): string | undefined {
    let result = baseText;
    if (result !== undefined) {
      const variableInBrackets = new RegExp(/({\w*})/g);
      let matchValues = result.match(variableInBrackets);
      matchValues?.forEach((match) => {
        const actualValue = replaceObj[match.substring(1, match.length - 1)];
        if (actualValue) {
          result = result?.replace(match, actualValue); // remove brackets
        }
      });
    }

    return result;
  }

  protected tooltipText(chargingOption: ChargingOptionInterface): string {
    const locale = `${this.locale.language}-${this.locale.country}`;
    const informationTranslation = this.replaceTempVars(chargingOption.information, {
      power: chargingOption.power?.toLocaleString(locale),
      dcPower: this.selectedVehicle.installedDcChargingPower?.toLocaleString(locale),
      acPower: this.selectedVehicle.installedAcChargingPower?.toLocaleString(locale),
    });
    const disclaimerTranslation = this.replaceTempVars(chargingOption.disclaimer, {
      power: chargingOption.power?.toLocaleString(locale),
      voltage: chargingOption.voltage?.toLocaleString(locale),
      ampere: chargingOption.current?.toLocaleString(locale),
      phases: chargingOption.phases?.toLocaleString(locale),
    });
    if (!informationTranslation && !disclaimerTranslation) return '';
    return `${informationTranslation} ${
      informationTranslation && disclaimerTranslation ? '<br /><br />' : ''
    } ${disclaimerTranslation}`;
  }

  protected labelText(chargingOption: ChargingOptionInterface): string {
    const locale = `${this.locale.language}-${this.locale.country}`;
    const labelTranslation = this.replaceTempVars(chargingOption.label, {
      power: chargingOption.power?.toLocaleString(locale),
    });
    return `${labelTranslation}`;
  }

  protected getAriaLabelForChargingOption(chargingOption: BatteryChangingInformation): string {
    const { hours, minutes } = timeConvert(chargingOption.loadingDurationMinutes);
    return this.$t('CHARGING_OPTIONS.ARIA_LABEL', {
      label: chargingOption.label,
      hours,
      hoursUnit: this.$t('CHARGING_OPTIONS.UNIT.HOURS_FULL'),
      minutes,
      minutesUnit: this.$t('CHARGING_OPTIONS.UNIT.MINUTES_FULL'),
    })?.toString();
  }

  protected getChargingOptions(chargingOptions: ChargingOptionInterface[]): ChargingOptionInterface[] {
    return chargingOptions.filter((option: ChargingOptionInterface) => {
      return this.dcChargingPowerIsAvailable || (!this.dcChargingPowerIsAvailable && !option.isDC);
    });
  }
}
